import { defineStore } from 'pinia'
import { ref } from 'vue'

// ================= Types =================
export type UserLevel = 'ADMIN' | 'SG' | 'SGA' | 'AMBASSADEUR' | 'DIRECTEUR' | 'CHEF_DEPT' | 'CHEF_SERVICE' | 'AGENT' | 'SECRETARIAT'

export interface Role {
  code: string
  nom: string
  niveau_hierarchique: number
  permissions: Record<string, boolean>
}

export interface Direction {
  id: number
  code: string
  nom: string
}

export interface Ambassade {
  id: number
  code: string
  nom: string
  pays?: string
  ville?: string
}

// Permissions granulaires du backoffice (renvoyées par /api/auth/check).
// full = profil pleinement admin (tout autorisé) ; sinon sections/actions listées.
export interface BackofficePermissions {
  full: boolean
  sections: string[]
  actions: string[]
}

export interface User {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  is_staff: boolean
  is_superuser: boolean
  role: Role | null
  direction: Direction | null
  ambassade: Ambassade | null
  two_factor_enabled?: boolean
  backoffice_permissions?: BackofficePermissions
}

// Résultat des étapes d'authentification (login / vérification 2FA)
export type LoginResult =
  | { status: 'success' }
  | { status: 'otp_required' }       // 2FA active : un code TOTP est attendu
  | { status: 'setup_required' }     // 2FA obligatoire mais pas encore configurée
  | { status: 'expired'; error: string }
  | { status: 'error'; error: string }

// ================= Config =================
const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:8000'

// ================= Store =================
export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const isAuthenticated = ref(false)
  const loading = ref(true)

  const canAccessBackoffice = (userData?: User | null) => {
    const roleCode = userData?.role?.code
    if (roleCode === 'ADMIN' || roleCode === 'DIRECTEUR' || roleCode === 'AMBASSADEUR') return true
    // Profil restreint (ex. support) : accès accordé si des sections sont attribuées.
    const perms = userData?.backoffice_permissions
    return !!(perms && perms.sections && perms.sections.length > 0)
  }

  // -------- Permissions granulaires (source : backoffice_permissions) --------
  // Rétro-compatible : si le backend ne renvoie pas le champ (ancienne version),
  // on ne restreint rien (comportement historique = tout autorisé).
  const isFullBackoffice = () => {
    const perms = user.value?.backoffice_permissions
    return !perms || perms.full === true
  }
  const canSection = (section?: string | null) => {
    if (!section) return true
    const perms = user.value?.backoffice_permissions
    if (!perms || perms.full) return true
    return !!perms.sections?.includes(section)
  }
  const canAction = (action: string) => {
    const perms = user.value?.backoffice_permissions
    if (!perms || perms.full) return true
    return !!perms.actions?.includes(action)
  }

  // -------- Vérification session existante --------
  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/check/`, {
        credentials: 'include',
      })

      if (response.ok) {
        const data = await response.json()
        if (data.authenticated && data.user) {
          if (!canAccessBackoffice(data.user)) {
            user.value = null
            isAuthenticated.value = false
            return
          }

          user.value = data.user
          isAuthenticated.value = true
        }
      }
    } catch (error) {
      console.error('Erreur vérification auth:', error)
    } finally {
      loading.value = false
    }
  }

  // -------- État de la double authentification (TOTP) --------
  // Tokens à usage unique délivrés par le backend pendant le flux de connexion.
  const pendingToken = ref<string | null>(null)   // 2FA active : valider un code
  const setupToken = ref<string | null>(null)      // 2FA à configurer : afficher le QR
  const twoFactorAccount = ref<string | null>(null) // email/username affiché pendant le setup

  // Applique un utilisateur connecté (réponse contenant data.user) après
  // vérification de l'accès backoffice. Retourne un résultat de login.
  const applyLoggedInUser = (userData: User | null): LoginResult => {
    if (!canAccessBackoffice(userData)) {
      user.value = null
      isAuthenticated.value = false
      return {
        status: 'error',
        error: 'Accès réservé aux administrateurs du backoffice',
      }
    }
    user.value = userData
    isAuthenticated.value = true
    pendingToken.value = null
    setupToken.value = null
    return { status: 'success' }
  }

  // -------- Login (étape 1 : identifiants) --------
  const login = async (
    username: string,
    password: string
  ): Promise<LoginResult> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      })

      const data = await response.json()

      // Cas 1 : 2FA déjà active → un code TOTP est requis
      if (data.two_factor_required && data.pending_token) {
        pendingToken.value = data.pending_token
        setupToken.value = null
        twoFactorAccount.value = data.account || username
        return { status: 'otp_required' }
      }

      // Cas 2 : 2FA obligatoire mais pas encore configurée → afficher le QR
      if (data.two_factor_setup_required && data.setup_token) {
        setupToken.value = data.setup_token
        pendingToken.value = null
        twoFactorAccount.value = data.account || username
        return { status: 'setup_required' }
      }

      // Cas 3 : connexion directe réussie (pas de 2FA)
      if (response.ok && data.success) {
        return applyLoggedInUser(data.user)
      }

      return {
        status: 'error',
        error: data.error || 'Identifiants incorrects',
      }
    } catch (error) {
      console.error('Erreur login:', error)
      return { status: 'error', error: 'Erreur de connexion au serveur' }
    }
  }

  // -------- Login (étape 2 : validation du code TOTP) --------
  const verifyLogin = async (code: string): Promise<LoginResult> => {
    if (!pendingToken.value) {
      return { status: 'error', error: 'Session expirée, veuillez vous reconnecter' }
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/2fa/verify-login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pending_token: pendingToken.value, code }),
      })
      const data = await response.json()

      if (response.ok && data.success) {
        return applyLoggedInUser(data.user)
      }

      if (data.code === 'pending_token_expired') {
        pendingToken.value = null
        return { status: 'expired', error: data.error || 'Session expirée' }
      }
      return { status: 'error', error: data.error || 'Code incorrect' }
    } catch (error) {
      console.error('Erreur verify 2FA:', error)
      return { status: 'error', error: 'Erreur de connexion au serveur' }
    }
  }

  // -------- Setup 2FA : génère un secret + QR code --------
  // Fonctionne soit pendant le flux de login forcé (setup_token), soit pour un
  // utilisateur déjà connecté (gestion depuis la page Sécurité).
  const setup2FA = async (forceNew = false): Promise<{ success: boolean; qrCode?: string; secret?: string; error?: string }> => {
    try {
      const body: Record<string, string | boolean> = {}
      if (setupToken.value) body.setup_token = setupToken.value
      if (forceNew) body.force_new = true

      const response = await fetch(`${API_BASE_URL}/api/auth/2fa/setup/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      })
      const data = await response.json()

      if (response.ok && data.success) {
        twoFactorAccount.value = data.account || twoFactorAccount.value
        return { success: true, qrCode: data.qr_code, secret: data.secret }
      }
      return { success: false, error: data.error || 'Impossible de générer le QR code' }
    } catch (error) {
      console.error('Erreur setup 2FA:', error)
      return { success: false, error: 'Erreur de connexion au serveur' }
    }
  }

  // -------- Confirme l'activation de la 2FA avec le 1er code --------
  // Si un setup_token est présent (flux login forcé), l'utilisateur est connecté
  // en même temps → on applique l'utilisateur renvoyé.
  const confirm2FA = async (code: string): Promise<LoginResult & { activated?: boolean }> => {
    try {
      const body: Record<string, string> = { code }
      if (setupToken.value) body.setup_token = setupToken.value

      const response = await fetch(`${API_BASE_URL}/api/auth/2fa/confirm/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      })
      const data = await response.json()

      if (response.ok && data.success) {
        // Cas login forcé : le backend renvoie l'utilisateur connecté
        if (data.user) {
          return { ...applyLoggedInUser(data.user), activated: true }
        }
        // Cas gestion (déjà connecté) : juste activé
        if (user.value) user.value.two_factor_enabled = true as any
        return { status: 'success', activated: true }
      }

      if (data.code === 'setup_token_expired') {
        setupToken.value = null
        return { status: 'expired', error: data.error || 'Lien de configuration expiré' }
      }
      return { status: 'error', error: data.error || 'Code incorrect' }
    } catch (error) {
      console.error('Erreur confirm 2FA:', error)
      return { status: 'error', error: 'Erreur de connexion au serveur' }
    }
  }

  // -------- Désactive la 2FA (utilisateur connecté, code requis) --------
  const disable2FA = async (code: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/2fa/disable/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code }),
      })
      const data = await response.json()
      if (response.ok && data.success) {
        if (user.value) user.value.two_factor_enabled = false as any
        return { success: true }
      }
      return { success: false, error: data.error || 'Code incorrect' }
    } catch (error) {
      console.error('Erreur disable 2FA:', error)
      return { success: false, error: 'Erreur de connexion au serveur' }
    }
  }

  // -------- État 2FA de l'utilisateur connecté --------
  const status2FA = async (): Promise<{ enabled: boolean; confirmedAt: string | null }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/2fa/status/`, {
        credentials: 'include',
      })
      const data = await response.json()
      if (response.ok && data.success) {
        return { enabled: !!data.enabled, confirmedAt: data.confirmed_at || null }
      }
      return { enabled: false, confirmedAt: null }
    } catch {
      return { enabled: false, confirmedAt: null }
    }
  }

  // -------- Logout --------
  const logout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout/`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch (error) {
      console.error('Erreur logout:', error)
    } finally {
      user.value = null
      isAuthenticated.value = false
    }
  }

  return {
    user,
    isAuthenticated,
    loading,
    pendingToken,
    setupToken,
    twoFactorAccount,
    isFullBackoffice,
    canSection,
    canAction,
    checkAuth,
    login,
    verifyLogin,
    setup2FA,
    confirm2FA,
    disable2FA,
    status2FA,
    logout,
  }
})

// ================= Helpers (hors store) =================
export function canAccessTab(user: User | null, tab: string): boolean {
  if (!user || !user.role) return false

  const roleCode = user.role.code

  // ADMIN global, DIRECTEUR de direction et AMBASSADEUR ont accès au backoffice
  if (roleCode === 'ADMIN' || roleCode === 'DIRECTEUR' || roleCode === 'AMBASSADEUR') return true

  switch (tab) {
    case 'Activités':
    case 'Archives':
      return true

    case 'Notes Arrivées':
      return roleCode === 'SECRETARIAT'

    case 'Notes Départs':
    case 'Parapheur':
    case 'Câbles':
      return roleCode !== 'SECRETARIAT'

    default:
      return false
  }
}

export function getVisibleTabs(user: User | null): string[] {
  const allTabs = ['Activités', 'Notes Départs', 'Câbles', 'Notes Arrivées', 'Parapheur', 'Archives']
  return allTabs.filter((tab) => canAccessTab(user, tab))
}

export function getUserDisplayLevel(user: User | null): string {
  if (!user || !user.role) return ''

  const niveauMap: Record<string, string> = {
    ADMIN: 'ADMIN',
    SG: 'N5',
    SGA: 'N4',
    AMBASSADEUR: 'N3',
    DIRECTEUR: 'N3',
    CHEF_DEPT: 'N2',
    CHEF_SERVICE: 'N2',
    AGENT: 'N1',
    SECRETARIAT: 'SECRETARIAT',
  }

  return niveauMap[user.role.code] || user.role.code
}
